import { InsuranceIndicationStatus, Vendor } from '../../models/insuranceTypes'
import type { CarrierCard } from './carrierCard'

const LOGO_BASE = 'https://static.cai.insure/carriers'

interface GenericCarrier {
  id: string
  vendor: Vendor
  vendorName: string
  carrierName: string | null
  iconSlug: string
  perTruckLow: number
  perTruckHigh: number
  /** Carriers that only write drivers with 2+ years of CDL experience. */
  requiresTwoYearCdl: boolean
}

// Generic per-truck bands for a brand-new authority. There's no CAB/DOT data to
// price against, so these are rough market ranges (not carrier-specific quotes);
// the CDL-experience answer gates which carriers are offered. Canal is
// intentionally excluded (per product direction).
const GENERIC_CARRIERS: GenericCarrier[] = [
  {
    id: 'geico',
    vendor: Vendor.GEICO,
    vendorName: 'GEICO',
    carrierName: null,
    iconSlug: 'geico',
    perTruckLow: 9000,
    perTruckHigh: 18000,
    requiresTwoYearCdl: false,
  },
  {
    id: 'progressive',
    vendor: Vendor.ROCKLAKE,
    vendorName: 'Progressive',
    carrierName: 'Progressive',
    iconSlug: 'progressive',
    perTruckLow: 11000,
    perTruckHigh: 20000,
    requiresTwoYearCdl: false,
  },
  {
    id: 'coverwhale',
    vendor: Vendor.COVERWHALE,
    vendorName: 'Cover Whale',
    carrierName: null,
    iconSlug: 'coverwhale',
    perTruckLow: 10000,
    perTruckHigh: 15000,
    requiresTwoYearCdl: true,
  },
]

function eligible(hasTwoYearCdl: boolean): GenericCarrier[] {
  return GENERIC_CARRIERS.filter((c) => hasTwoYearCdl || !c.requiresTwoYearCdl)
}

/** Generic estimate cards for the chosen CDL answer, in the funnel card shape. */
export function genericEstimateCards(hasTwoYearCdl: boolean): CarrierCard[] {
  return eligible(hasTwoYearCdl).map((c) => ({
    id: c.id,
    vendor: c.vendor,
    vendorName: c.vendorName,
    status: InsuranceIndicationStatus.GENERATED,
    iconUrl: `${LOGO_BASE}/${c.iconSlug}-icon.png`,
    iconAlt: c.vendorName,
    carrierName: c.carrierName,
    truckCount: null,
    perTruckLow: c.perTruckLow,
    perTruckHigh: c.perTruckHigh,
    fleetLow: null,
    fleetHigh: null,
    carrierNote: null,
    carrierPublicNote: null,
    declineReason: null,
    isCurrentInsurer: false,
    tagline: null,
  }))
}

/** Carrier names available for a CDL answer, for the question-screen copy. */
export function eligibleCarrierNames(hasTwoYearCdl: boolean): string[] {
  return eligible(hasTwoYearCdl).map((c) => c.vendorName)
}
